import { contentBlockRenderer } from "../renderers/ContentBlockRenderer";

/**
 * 解析邮件主题模板中的行内内容块
 * @param {string} subjectTemplate - 包含行内内容块的主题模板
 * @param {Object} businessContext - 业务上下文数据
 * @returns {string} 解析后的主题文本
 */
export const parseSubjectTemplate = (subjectTemplate, businessContext) => {
    if (!subjectTemplate || typeof subjectTemplate !== "string") {
        return subjectTemplate || "";
    }

    // 如果不包含内容块标记，直接返回
    if (!subjectTemplate.includes("data-content-block-type")) {
        return subjectTemplate;
    }

    try {
        // 创建临时DOM元素解析HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = subjectTemplate;

        // 查找所有行内内容块
        const inlineBlocks = tempDiv.querySelectorAll("[data-content-block-type]");

        inlineBlocks.forEach((element) => {
            const blockType = element.getAttribute("data-content-block-type");
            const blockId = element.getAttribute("data-content-block-id");
            const dataKey = element.getAttribute("data-key");

            // 根据内容块类型和业务数据生成文本
            const generatedText = generateInlineContentText(blockType, blockId, dataKey, businessContext);

            // 用生成的文本替换内容块元素
            element.replaceWith(document.createTextNode(generatedText));
        });

        // 返回纯文本内容
        return tempDiv.textContent || tempDiv.innerText || "";
    } catch (error) {
        console.error("解析主题模板时出错:", error);
        // 出错时返回原始模板的纯文本版本
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = subjectTemplate;
        return tempDiv.textContent || tempDiv.innerText || subjectTemplate;
    }
};

/**
 * 根据内容块类型生成行内文本
 * 复用 ContentBlockNodeView 中的 renderInlineContent 逻辑
 * @param {string} blockType - 内容块类型
 * @param {string} blockId - 内容块ID
 * @param {string} dataKey - 数据键
 * @param {Object} businessContext - 业务上下文数据
 * @returns {string} 生成的文本内容
 */
const generateInlineContentText = (blockType, blockId, dataKey, businessContext) => {
    try {
        // 获取对应的业务数据 - 使用多策略查找
        let businessData = null;
        
        // 策略1-3: 尝试从 contentBlocks 数组获取
        if (businessContext.contentBlocks && businessContext.contentBlocks.length > 0) {
             // 策略1: 按 id 精确匹配
            const blockById = businessContext.contentBlocks.find((cb) => cb.id === blockId);
            if (blockById?.data) {
                businessData = blockById.data;
            } else {
                // 策略2: 按 type 匹配
                const blockByType = businessContext.contentBlocks.find((cb) => cb.type === blockType);
                if (blockByType?.data) {
                    businessData = blockByType.data;
                }
            }
        }
        
        // 策略4: 从顶层数据兜底（如果尚未找到）
        if (!businessData) {
            switch (blockType) {
                case "operationFlightList":
                    businessData = businessContext.flightOperations || 
                                  businessContext.operations || 
                                  businessContext.rawData?.flights || 
                                  businessContext.dataPayload;
                    break;
                case "flightWithPredecessor":
                    businessData = businessContext.flightsWithPredecessor || 
                                  businessContext.flightOperations || 
                                  businessContext.rawData?.flights || 
                                  businessContext.dataPayload;
                    break;
                case "scheduleFlightList":
                case "scheduleFlightListWithComparison":
                    businessData = businessContext.flights || 
                                  businessContext.comparisonData || 
                                  (businessContext.schedules ? { flights: businessContext.schedules } : null);
                    break;
                case "scheduleInfo":
                    businessData = businessContext.schedule || businessContext.schedule_info;
                    break;
                default:
                    break;
            }
        }

        // 调用统一的渲染服务获取HTML内容
        // 关键：将 blockId 作为 contentBlockId 传递，用于区分不同用途的内容块
        const options = {
            ...businessContext,
            contentBlockId: blockId,
        };
        const htmlContent = contentBlockRenderer.renderInlineText(blockType, businessData, options);

        // 如果返回的是HTML内容，提取纯文本（邮件主题不应该包含HTML）
        if (typeof htmlContent === "string" && htmlContent.includes("<")) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlContent;
            return tempDiv.textContent || tempDiv.innerText || htmlContent;
        }

        return htmlContent;
    } catch (error) {
        console.error(`生成内容块文本时出错 (${blockType}:${blockId}):`, error);
        return blockId || blockType || "Content";
    }
};

/**
 * 验证主题模板是否包含有效的内容块
 * @param {string} subjectTemplate - 主题模板
 * @returns {boolean} 是否包含有效的内容块
 */
export const hasValidContentBlocks = (subjectTemplate) => {
    if (!subjectTemplate || typeof subjectTemplate !== "string") {
        return false;
    }

    try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = subjectTemplate;
        const blocks = tempDiv.querySelectorAll("[data-content-block-type]");
        return blocks.length > 0;
    } catch (error) {
        return false;
    }
};

/**
 * 提取主题模板中的内容块信息
 * @param {string} subjectTemplate - 主题模板
 * @returns {Array} 内容块信息数组
 */
export const extractSubjectContentBlocks = (subjectTemplate) => {
    if (!subjectTemplate || typeof subjectTemplate !== "string") {
        return [];
    }

    try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = subjectTemplate;
        const blocks = tempDiv.querySelectorAll("[data-content-block-type]");

        return Array.from(blocks).map((element, index) => ({
            type: element.getAttribute("data-content-block-type"),
            id: element.getAttribute("data-content-block-id"),
            label: element.getAttribute("data-content-block-label") || element.textContent,
            dataKey: element.getAttribute("data-key"),
            index,
        }));
    } catch (error) {
        console.error("提取主题内容块时出错:", error);
        return [];
    }
};
